import { produce } from "immer";
import { sortBy } from "lodash";
import { useEffect, useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { CatalogItem, CatalogItemSequenceType, ProductCatalog, UnitType } from "types";
import { useModal } from "@/hooks/useVisibility";
import { getResultingSequence } from "@/models/CatalogItem";
import { reorder } from "@/utils/reorder";

interface UseCatalogItemListProps<T extends CatalogItem> {
    value: ProductCatalog;
    catalogItems: T[];
    unitType: UnitType;
    onChange: (catalog: ProductCatalog) => void;
    onDeleteCatalogItem: (catalogItem: T) => void;
    onUpdateCatalogItem: (catalogItem: T) => void;
}

export const useDraggableCatalogItems = <T extends CatalogItem>({
    value,
    catalogItems,
    unitType,
    onChange,
    onDeleteCatalogItem,
    onUpdateCatalogItem,
}: UseCatalogItemListProps<T>) => {
    const { isVisible: showEditCatalogItemModal, hide: closeEditModal, toggle: toggleModal } = useModal(false);
    const [selectedCatalogItem, setSelectedCatalogItem] = useState<T>();
    const [items, setItems] = useState<T[]>(catalogItems);

    useEffect(() => {
        setItems(sortBy(catalogItems, "sequence"));
    }, [catalogItems]);

    const toggleEditCatalogItemModal = (_event: any, catalogItem?: T) => {
        setSelectedCatalogItem(catalogItem);
        toggleModal();
    };

    const handleSubmit = (catalogItem: T) => {
        onUpdateCatalogItem(catalogItem);
        setSelectedCatalogItem(undefined);
        closeEditModal();
    };

    const updateList = (updated: T[]) => {
        setItems(updated);
        const catalog = produce(value, (draft) => {
            draft.items = [...draft.items.filter((item) => item.unitType !== unitType), ...updated];
        });
        onChange(catalog);
    };

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }
        const resultingSequence = getResultingSequence({
            destinationIndex: result.destination.index,
            sourceIndex: result.source.index,
            productCatalogItems: items,
            type:
                unitType === UnitType.EQUIPMENT
                    ? CatalogItemSequenceType.EQUIPMENT
                    : CatalogItemSequenceType.DEMOGRAPHICS,
        });
        const reordered = reorder(items, result.source.index, result.destination.index).map((item) =>
            item.name === result.draggableId ? { ...item, sequence: resultingSequence } : item,
        );
        updateList(reordered);
    };

    const handleItemDelete = (index: number, item: T) => {
        updateList(items.filter((_, index_) => index_ !== index));
        onDeleteCatalogItem(item);
    };

    return {
        items,
        showEditCatalogItemModal,
        selectedCatalogItem,
        toggleEditCatalogItemModal,
        handleSubmit,
        handleDragEnd,
        handleItemDelete,
    };
};
